/* eslint-disable */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { ColorChartData, CellDimensions } from '../components/ColorChartCanvas'
import { fonts, FontStyle } from '../utils/fonts'

interface UseColorChartOptions {
  initialWidth?: number;
  initialHeight?: number;
  initialData?: ColorChartData;
  initialTextData?: Record<string, string>;
  cellDimensions: CellDimensions;
}

export function useColorChart({ 
  initialWidth = 10, 
  initialHeight = 10,
  initialData,
  initialTextData,
  cellDimensions 
}: UseColorChartOptions) {
  const [data, setData] = useState<ColorChartData>(initialData || {});
  const [width, setWidth] = useState<number>(initialWidth);
  const [height, setHeight] = useState<number>(initialHeight);
  const [color, setColor] = useState<string>('#000000');
  const [brushSize, setBrushSize] = useState<number>(1);
  const [isPainting, setIsPainting] = useState<boolean>(false);
  const [zoom, setZoom] = useState<number>(1);
  const [tool, setTool] = useState<'brush' | 'bucket' | 'text'>('brush');
  const [textData, setTextData] = useState<Record<string, string>>(initialTextData || {});
  const [textInput, setTextInput] = useState<string>('');
  const [fontStyle, setFontStyle] = useState<FontStyle>('standard');
  const [textAlignment, setTextAlignment] = useState<'left' | 'center' | 'right'>('left');
  const [history, setHistory] = useState<ColorChartData[]>([initialData || {}]);
  const [historyIndex, setHistoryIndex] = useState<number>(0);
  const canvasRef = useRef<HTMLDivElement>(null);
  const lastPaintedCellRef = useRef<[number, number] | null>(null);

  // Save to history
  const saveToHistory = (newData: ColorChartData): void => {
    // Use structured clone for better performance than JSON.parse/stringify
    // Falls back to JSON method for older browsers
    const dataCopy = typeof structuredClone !== 'undefined' 
      ? structuredClone(newData) 
      : JSON.parse(JSON.stringify(newData));
    
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(dataCopy);
      if (newHistory.length > 50) {
        newHistory.shift();
        setHistoryIndex(prev => Math.max(0, prev - 1));
        return newHistory;
      }
      return newHistory;
    });
    setHistoryIndex(prev => prev + 1);
  }

  // Undo/Redo
  const undo = (): void => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setData(typeof structuredClone !== 'undefined' 
        ? structuredClone(history[newIndex]) 
        : JSON.parse(JSON.stringify(history[newIndex])));
    }
  }

  const redo = (): void => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setData(typeof structuredClone !== 'undefined' 
        ? structuredClone(history[newIndex]) 
        : JSON.parse(JSON.stringify(history[newIndex])));
    }
  }

  // Paint at position
  const paintAt = (i: number, j: number, saveHistory: boolean = false): void => {
    setData(prev => {
      const updated = { ...prev };
      const startOffset = Math.floor((brushSize - 1) / 2);
      const endOffset = Math.floor(brushSize / 2);

      for (let r = i - startOffset; r <= i + endOffset; r++) {
        for (let c = j - startOffset; c <= j + endOffset; c++) {
          if (r >= 0 && r < height && c >= 0 && c < width) {
            updated[`${r}_${c}`] = { backgroundColor: color };
          }
        }
      }
      
      if (saveHistory) {
        saveToHistory(updated);
      }
      
      return updated;
    });
  }

  // Bucket fill
  const bucketFill = (startI: number, startJ: number): void => {
    const startKey = `${startI}_${startJ}`;
    const targetColor = data[startKey]?.backgroundColor || '#e0e0e0';
    
    if (targetColor === color) {
      return;
    }

    saveToHistory(data);
    
    setData(prev => {
      const updated = { ...prev };
      const queue: Array<[number, number]> = [[startI, startJ]];
      const visited = new Set<string>();
      
      while (queue.length > 0) {
        const [i, j] = queue.shift()!;
        const key = `${i}_${j}`;
        
        if (i < 0 || i >= height || j < 0 || j >= width || visited.has(key)) {
          continue;
        }
        
        const currentColor = prev[key]?.backgroundColor || '#e0e0e0';
        if (currentColor !== targetColor) {
          continue;
        }
        
        updated[key] = { backgroundColor: color };
        visited.add(key);
        
        queue.push([i - 1, j]);
        queue.push([i + 1, j]);
        queue.push([i, j - 1]);
        queue.push([i, j + 1]);
      }
      
      return updated;
    });
  }

  // Get color at position
  const getColorAt = (i: number, j: number): string => {
    return data[`${i}_${j}`]?.backgroundColor || '#e0e0e0';
  }

  // Get all cells between two points (line interpolation)
  const getCellsBetween = (x0: number, y0: number, x1: number, y1: number): Array<[number, number]> => {
    const cells: Array<[number, number]> = [];
    const dx = Math.abs(x1 - x0);
    const dy = Math.abs(y1 - y0);
    const sx = x0 < x1 ? 1 : -1;
    const sy = y0 < y1 ? 1 : -1;
    let err = dx - dy;
    let x = x0;
    let y = y0;

    while (true) {
      cells.push([x, y]);
      if (x === x1 && y === y1) break;
      const e2 = 2 * err;
      if (e2 > -dy) {
        err -= dy;
        x += sx;
      }
      if (e2 < dx) {
        err += dx;
        y += sy;
      }
    }
    return cells;
  }

  // Get text at position
  const getTextAt = (i: number, j: number): string | undefined => {
    return textData[`${i}_${j}`];
  }

  // Mouse handlers
  const handleMouseDown = (i: number, j: number): void => {
    if (tool === 'text') {
      placeText(i, j);
    } else if (tool === 'bucket') {
      bucketFill(i, j);
    } else {
      setIsPainting(true);
      lastPaintedCellRef.current = [i, j];
      saveToHistory(data);
      paintAt(i, j, false);
    }
  }

  const handleMouseEnter = (i: number, j: number): void => {
    if (tool === 'bucket' || !isPainting) return;
    
    const lastCell = lastPaintedCellRef.current;
    if (lastCell && (lastCell[0] !== i || lastCell[1] !== j)) {
      // Paint all cells between last position and current position
      const cells = getCellsBetween(lastCell[0], lastCell[1], i, j);
      cells.forEach(([r, c]) => {
        paintAt(r, c, false);
      });
    } else {
      paintAt(i, j, false);
    }
    
    lastPaintedCellRef.current = [i, j];
  }

  const handleMouseUp = (): void => {
    if (isPainting && tool === 'brush') {
      saveToHistory(data);
    }
    setIsPainting(false);
    lastPaintedCellRef.current = null;
  }

  // Reset painting on window mouseup
  useEffect(() => {
    const stopPainting = () => {
      setIsPainting(false);
      lastPaintedCellRef.current = null;
    };
    window.addEventListener("mouseup", stopPainting);
    return () => window.removeEventListener("mouseup", stopPainting);
  }, [])

  // Fit to view
  const fitToView = (): void => {
    if (canvasRef.current && width && height) {
      const container = canvasRef.current;
      const containerWidth = container.clientWidth - 32;
      const containerHeight = container.clientHeight - 32;
      const chartWidth = width * (cellDimensions.width + cellDimensions.margin * 2);
      const chartHeight = height * (cellDimensions.height + cellDimensions.margin * 2);
      const scaleX = containerWidth / chartWidth;
      const scaleY = containerHeight / chartHeight;
      setZoom(Math.min(scaleX, scaleY, 1) * 0.95);
    }
  }

  // Helper function to get leftmost content column (for alignment)
  // Memoized to avoid recalculation
  const getLeftmostCol = useCallback((pattern: number[][]): number => {
    if (!pattern || pattern.length === 0) return 0;
    
    let leftmost = Infinity;
    for (let row = 0; row < pattern.length; row++) {
      for (let col = 0; col < pattern[row].length; col++) {
        if (pattern[row][col] === 1) {
          leftmost = Math.min(leftmost, col);
        }
      }
    }
    
    return leftmost === Infinity ? 0 : leftmost;
  }, []);

  // Helper function to get character width (actual content width, excluding padding zeros)
  // Memoized to avoid recalculation
  const getCharWidth = useCallback((pattern: number[][]): number => {
    if (!pattern || pattern.length === 0) return 5;
    
    let leftmost = Infinity;
    let rightmost = -1;
    
    // Find the leftmost and rightmost columns that contain a filled pixel (1)
    for (let row = 0; row < pattern.length; row++) {
      for (let col = 0; col < pattern[row].length; col++) {
        if (pattern[row][col] === 1) {
          leftmost = Math.min(leftmost, col);
          rightmost = Math.max(rightmost, col);
        }
      }
    }
    
    // If no pixels found, return default width
    if (leftmost === Infinity || rightmost === -1) return 5;
    
    // Return actual content width (rightmost - leftmost + 1)
    return rightmost - leftmost + 1;
  }, []);

  // Helper function to calculate line width
  // Memoized to avoid recalculation
  const calculateLineWidth = useCallback((line: string, fontPatterns: Record<string, number[][]>): number => {
    let width = 0;
    for (const char of line) {
      const pattern = fontPatterns[char];
      if (pattern) {
        const charWidth = getCharWidth(pattern);
        width += charWidth + 1; // char width + 1 space between characters
      }
    }
    return width > 0 ? width - 1 : 0; // Remove last trailing space
  }, [getCharWidth]);

  // Memoize font patterns lookup
  const fontPatterns = useMemo(() => {
    return fonts[fontStyle] || fonts.standard;
  }, [fontStyle]);

  // Helper function to get start column based on alignment
  const getStartCol = useCallback((line: string, startCol: number, patterns: Record<string, number[][]>): number => {
    const lineWidth = calculateLineWidth(line, patterns);
    if (textAlignment === 'left') {
      return startCol;
    } else if (textAlignment === 'center') {
      return startCol - Math.floor(lineWidth / 2);
    } else { // right
      return startCol - lineWidth;
    }
  }, [textAlignment, calculateLineWidth]);

  // Get text preview cells - returns array of [row, col] that would be filled
  // Memoized based on textInput, fontStyle, textAlignment, width, height
  const getTextPreviewCells = useCallback((i: number, j: number): Array<[number, number]> => {
    const cells: Array<[number, number]> = [];
    if (!textInput.trim()) return cells;

    const text = textInput; // Keep original text including newlines
    const lineSpacing = 1; // Space between lines
    
    const lines = text.split('\n');
    let currentRow = i;
    const startCol = j;

    // Process each line
    for (const line of lines) {
      if (!line.trim()) {
        // For empty lines, use standard height
        currentRow += 7 + lineSpacing;
        continue;
      }

      // Calculate maximum height in this line
      let maxLineHeight = 0;
      for (const char of line) {
        const pattern = fontPatterns[char];
        if (pattern) {
          maxLineHeight = Math.max(maxLineHeight, pattern.length);
        }
      }

      // Calculate start column based on alignment
      const lineStartCol = getStartCol(line, startCol, fontPatterns);
      let currentCol = lineStartCol;

      // Process each character in the line
      for (const char of line) {
        const pattern = fontPatterns[char];
        if (!pattern) continue; // Skip unknown characters

        // Get leftmost content column for alignment
        const leftmostCol = getLeftmostCol(pattern);

        // Collect cells for this character (aligned by leftmost content)
        for (let row = 0; row < pattern.length; row++) {
          for (let col = 0; col < pattern[row].length; col++) {
            if (pattern[row][col] === 1) {
              const cellRow = currentRow + row;
              const cellCol = currentCol - leftmostCol + col;

              // Check bounds
              if (cellRow >= 0 && cellRow < height && cellCol >= 0 && cellCol < width) {
                cells.push([cellRow, cellCol]);
              }
            }
          }
        }

        // Move to next character position (content width + 1 space for consistent spacing)
        const charWidth = getCharWidth(pattern);
        currentCol += charWidth + 1;
      }

      // Move to next line using the maximum height of this line
      currentRow += maxLineHeight + lineSpacing;
    }

    return cells;
  }, [textInput, fontPatterns, textAlignment, width, height, getStartCol, getLeftmostCol, getCharWidth]);

  // Check if text fits within bounds
  // Memoized based on textInput, fontStyle, textAlignment, width, height
  const canPlaceText = useCallback((i: number, j: number): boolean => {
    if (!textInput.trim()) return false;

    const text = textInput; // Keep original text including newlines
    const lineSpacing = 1; // Space between lines
    
    const lines = text.split('\n');
    let maxWidth = 0;
    let totalHeight = 0;

    // Calculate max width and total height
    for (const line of lines) {
      if (!line.trim()) {
        totalHeight += 7 + lineSpacing; // Standard height for empty lines
        continue;
      }
      
      const lineWidth = calculateLineWidth(line, fontPatterns);
      maxWidth = Math.max(maxWidth, lineWidth);
      
      // Calculate maximum height in this line
      let maxLineHeight = 0;
      for (const char of line) {
        const pattern = fontPatterns[char];
        if (pattern) {
          maxLineHeight = Math.max(maxLineHeight, pattern.length);
        }
      }
      totalHeight += maxLineHeight + lineSpacing;
    }
    
    // Remove last line spacing
    if (totalHeight > 0) {
      totalHeight -= lineSpacing;
    }

    // Check if text fits (considering alignment offset)
    if (i + totalHeight > height) {
      return false;
    }
    
    // Check if text fits horizontally (considering alignment)
    if (textAlignment === 'left') {
      if (j + maxWidth > width) return false;
    } else if (textAlignment === 'center') {
      if (j + Math.floor(maxWidth / 2) > width || j - Math.floor(maxWidth / 2) < 0) return false;
    } else { // right
      if (j - maxWidth < 0 || j > width) return false;
    }

    return true;
  }, [textInput, fontPatterns, textAlignment, width, height, calculateLineWidth]);

  // Place text at position - fills cells to form letters
  const placeText = (i: number, j: number): void => {
    if (!textInput.trim()) return;

    // Check if text fits
    if (!canPlaceText(i, j)) {
      alert('Text is too large for the chart. Please increase chart size or use shorter text.');
      return;
    }

    saveToHistory(data);

    setData(prev => {
      const updated = { ...prev };

      // Get cells from pixel patterns
      const text = textInput; // Keep original text including newlines
      const lineSpacing = 1; // Space between lines
      
      const lines = text.split('\n');
      let currentRow = i;
      const startCol = j;

      // Process each line
      for (const line of lines) {
        if (!line.trim()) {
          // For empty lines, use standard height
          currentRow += 7 + lineSpacing;
          continue;
        }

        // Calculate maximum height in this line
        let maxLineHeight = 0;
        for (const char of line) {
          const pattern = fontPatterns[char];
          if (pattern) {
            maxLineHeight = Math.max(maxLineHeight, pattern.length);
          }
        }

        // Calculate start column based on alignment
        const lineStartCol = getStartCol(line, startCol, fontPatterns);
        let currentCol = lineStartCol;

        // Process each character in the line
        for (const char of line) {
          const pattern = fontPatterns[char];
          if (!pattern) continue; // Skip unknown characters

          // Get leftmost content column for alignment
          const leftmostCol = getLeftmostCol(pattern);

          // Draw the character pattern (aligned by leftmost content)
          for (let row = 0; row < pattern.length; row++) {
            for (let col = 0; col < pattern[row].length; col++) {
              if (pattern[row][col] === 1) {
                const cellRow = currentRow + row;
                const cellCol = currentCol - leftmostCol + col;

                // Check bounds
                if (cellRow >= 0 && cellCol >= 0 && cellRow < height && cellCol < width) {
                  updated[`${cellRow}_${cellCol}`] = { backgroundColor: color };
                }
              }
            }
          }

          // Move to next character position (content width + 1 space for consistent spacing)
          const charWidth = getCharWidth(pattern);
          currentCol += charWidth + 1;
        }

        // Move to next line using the maximum height of this line
        currentRow += maxLineHeight + lineSpacing;
      }

      return updated;
    });
  };

  const reset = (): void => {
    setData({});
    setTextData({});
    setTextInput('');
    setFontStyle('standard');
    setTextAlignment('left');
    setWidth(initialWidth);
    setHeight(initialHeight);
    setColor('#000000');
    setBrushSize(1);
    setZoom(1);
    setTool('brush');
    setHistory([{}]);
    setHistoryIndex(0);
  };

  return {
    // State
    data,
    width,
    height,
    color,
    brushSize,
    zoom,
    tool,
    textData,
    textInput,
    fontStyle,
    textAlignment,
    canvasRef,
    canUndo: historyIndex > 0,
    canRedo: historyIndex < history.length - 1,
    // Setters
    setWidth,
    setHeight,
    setColor,
    setBrushSize,
    setZoom,
    setTool,
    setData,
    setTextInput,
    setFontStyle,
    setTextAlignment,
    // Actions
    undo,
    redo,
    paintAt,
    bucketFill,
    getColorAt,
    getTextAt,
    getTextPreviewCells,
    canPlaceText,
    placeText,
    handleMouseDown,
    handleMouseEnter,
    handleMouseUp,
    fitToView,
    saveToHistory,
    reset
  };
}

